<template>
  <div class="trt-period-nav">
    <button @click="emit('shiftPeriod', -1)" class="trt-arrow-btn">
      <ChevronLeftIcon class="h-3.5 w-3.5 stroke-[2.5]" />
    </button>

    <div class="trt-period-label-container">
      <Transition :name="periodTransition" mode="out-in">
        <span :key="periodLabel" class="trt-period-label">
          {{ periodLabel }}
        </span>
      </Transition>
    </div>

    <button
      @click="emit('shiftPeriod', 1)"
      :disabled="periodOffset >= 0"
      class="trt-arrow-btn"
    >
      <ChevronRightIcon class="h-3.5 w-3.5 stroke-[2.5]" />
    </button>

    <Transition name="trt-reset-fade">
      <button
        v-if="periodOffset !== 0"
        @click="emit('resetPeriod')"
        class="trt-reset-pill"
      >
        {{ t('editcomponent.sheet.reset_period') }}
      </button>
    </Transition>
  </div>
</template>

<script setup lang="ts">
defineOptions({ name: 'TopbarPeriodNav' })

import { useI18n } from 'vue-i18n'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'

withDefaults(defineProps<{
  periodLabel: string
  periodOffset?: number
  periodTransition?: string
}>(), {
  periodOffset: 0,
  periodTransition: 'trt-label-slide-left',
})

const emit = defineEmits<{
  shiftPeriod: [direction: number]
  resetPeriod: []
}>()

const { t } = useI18n()
</script>

<!-- Unscoped: period nav may be teleported outside this component's tree -->
<style>
/* ── Period navigation ── */
.trt-period-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.trt-arrow-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.15s ease;
}

.trt-arrow-btn:hover:not(:disabled) {
  color: var(--foreground);
  background: rgba(6, 200, 255, 0.08);
  border-color: rgba(6, 200, 255, 0.2);
}

.trt-arrow-btn:active:not(:disabled) {
  transform: scale(0.92);
  background: rgba(6, 200, 255, 0.12);
}

.trt-arrow-btn:disabled {
  opacity: 0.2;
  cursor: default;
}

:is(.dark *).trt-arrow-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.06);
}

:is(.dark *).trt-arrow-btn:hover:not(:disabled) {
  background: rgba(6, 200, 255, 0.1);
  border-color: rgba(6, 200, 255, 0.22);
}

.trt-period-label-container {
  position: relative;
  width: 120px;
  height: 22px;
  overflow: hidden;
}

.trt-period-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--foreground);
  white-space: nowrap;
}

.trt-reset-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 10px;
  border-radius: 20px;
  color: var(--apex-cyan);
  background: rgba(6, 200, 255, 0.08);
  border: 1px solid rgba(6, 200, 255, 0.15);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.trt-reset-pill:hover {
  background: rgba(6, 200, 255, 0.14);
}

.trt-reset-pill:active {
  transform: scale(0.95);
}

:is(.dark *).trt-reset-pill {
  background: rgba(6, 200, 255, 0.1);
  border-color: rgba(6, 200, 255, 0.2);
}

/* ── Period label slide transitions ── */
.trt-label-slide-left-enter-active,
.trt-label-slide-left-leave-active,
.trt-label-slide-right-enter-active,
.trt-label-slide-right-leave-active {
  transition: all 0.2s var(--ease-standard);
}
.trt-label-slide-left-enter-from { opacity: 0; transform: translateX(12px); }
.trt-label-slide-left-leave-to { opacity: 0; transform: translateX(-12px); }
.trt-label-slide-right-enter-from { opacity: 0; transform: translateX(-12px); }
.trt-label-slide-right-leave-to { opacity: 0; transform: translateX(12px); }

/* ── Reset pill fade ── */
.trt-reset-fade-enter-active { transition: all 0.2s ease; }
.trt-reset-fade-leave-active { transition: all 0.15s ease; }
.trt-reset-fade-enter-from,
.trt-reset-fade-leave-to { opacity: 0; transform: scale(0.9); }
</style>
